import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import { Box } from "@mui/system";
import { Checkbox, FormControlLabel } from "@mui/material";
import { downloadOcplcAction } from "actions/ocplcAction";
import {
  DASHBOARD_TITLE,
  DOWNLOAD_ALL,
  MATERIAL_AVAILABILITY,
  MODEL_AVAILABILITY_TABLE,
  REGIONS,
  ROLE_REGION_MAP,
} from "constants/ocplcConstants";

const getRegionKey = (type, region) => `${type}_${region.toLowerCase()}Region`;

const DownloadDialog = ({
  open,
  handleClose,
  checkedRows,
  productLineID,
  setCheckedRows,
  region,
  selectKmat,
  title,
  selectedTable,
  selectedCategory,
  dashboardrole,
  productGroup,
  selectedProductGroup,
  setisLoading,
  selectedIds,
  setSelectedIds
}) => {
  const dispatch = useDispatch();
  const [selectedItems, setSelectedItems] = useState([]);
  const tableData = useSelector((state) => state.ocplc.tableData);
  const options = [{ subCategory: DOWNLOAD_ALL }, ...tableData];
  const tableRegions = tableData
    .filter((item) => selectedItems.includes(item.subCategory))
    .map((item) => {
      // For each table, collect checked regions for model/material
      let checkedRegions = REGIONS.filter(
        (region) =>
          (selectedItems.includes(getRegionKey("model", region)) &&
            item.subCategory === MODEL_AVAILABILITY_TABLE) ||
          (selectedItems.includes(getRegionKey("material", region)) &&
            item.subCategory === MATERIAL_AVAILABILITY)
      );
      // Always include "WW"
      const regionList = ["WW", ...checkedRegions];
      return {
        tableName: item.subCategory,
        region: regionList,
      };
    });

  const disabled = title === DASHBOARD_TITLE && selectedItems.length === 0;

  const handleDownload = () => {
    dispatch(
      downloadOcplcAction(
        checkedRows,
        productLineID,
        setCheckedRows,
        handleClose,
        region,
        selectKmat,
        title,
        selectedTable,
        tableRegions,
        selectedCategory,
        productGroup,
        selectedProductGroup,
        setisLoading,
        setSelectedIds
      )
    )
  };
const handleCheckboxChange = (event) => {
  const { checked, name } = event.target;
  let newSelected = [...selectedItems];

  if (name === DOWNLOAD_ALL) {
    newSelected = checked
      ? [DOWNLOAD_ALL, ...tableData.map((item) => item.subCategory)]
      : [];
    // Check region for material and model based on dashboardrole
    const region = ROLE_REGION_MAP[dashboardrole];
    if (region && checked) {
      newSelected = [
        ...newSelected,
        getRegionKey("material", region),
        getRegionKey("model", region),
      ];
    }
  } else {
    if (checked) {
      newSelected.push(name);
    } else {
      newSelected = newSelected.filter((item) => item !== name);
    }

    // If dashboardrole matches and Material Availability is selected, check region for material
    const region = ROLE_REGION_MAP[dashboardrole];
    if (
      region &&
      checked &&
      name === MATERIAL_AVAILABILITY &&
      !newSelected.includes(getRegionKey("material", region))
    ) {
      newSelected.push(getRegionKey("material", region));
    }
    // If dashboardrole matches and Model Availability is selected, check region for model
    if (
      region &&
      checked &&
      name === MODEL_AVAILABILITY_TABLE &&
      !newSelected.includes(getRegionKey("model", region))
    ) {
      newSelected.push(getRegionKey("model", region));
    }

    // Auto-check Download All if all tables are selected
    const allTables = tableData.map((item) => item.subCategory);
    const selectedTables = newSelected.filter(
      (item) => allTables.includes(item)
    );
    if (
      selectedTables.length === allTables.length &&
      !newSelected.includes(DOWNLOAD_ALL)
    ) {
      newSelected.unshift(DOWNLOAD_ALL);
    }
    // Uncheck Download All if any table is deselected
    if (
      selectedTables.length < allTables.length &&
      newSelected.includes(DOWNLOAD_ALL)
    ) {
      newSelected = newSelected.filter((item) => item !== DOWNLOAD_ALL);
    }
  }
  setSelectedItems(newSelected);
};

  const handleRegionCheckbox = (type, region, checked) => {
    const regionKey = getRegionKey(type, region);
    setSelectedItems(
      checked
        ? [...selectedItems, regionKey]
        : selectedItems.filter((item) => item !== regionKey)
    );
  };

const shouldDisableCheckbox = (item) => {
  if (item.subCategory === DOWNLOAD_ALL) {
    // Never disable Download All
    return false;
  } else {
    // Disable other options if Download All is selected
    return selectedItems.includes(DOWNLOAD_ALL);
  }
};

  const renderRegionCheckboxes = (type, label) =>
    selectedItems.includes(label) && (
      <Box sx={{ mb: 2 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{label} Regions</div>
        {REGIONS.map((region) => (
          <FormControlLabel
            key={`${type}-${region}`}
            control={
              <Checkbox
                checked={selectedItems.includes(getRegionKey(type, region))}
                onChange={(e) =>
                  handleRegionCheckbox(type, region, e.target.checked)
                }
                name={getRegionKey(type, region)}
              />
            }
            label={region}
          />
        ))}
      </Box>
    );

  return (
    open && (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { borderRadius: 10, width: title === DASHBOARD_TITLE ? 'auto' : 480, padding: 7, margin: 0 },
        }}
      >
        <DialogTitle> Download </DialogTitle>
        <DialogContent>
          {title === DASHBOARD_TITLE ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: 1, display: "grid" }}>
                {options.map((item, idx) => (
                  <FormControlLabel
                    key={idx}
                    control={
                      <Checkbox
                        checked={selectedItems.includes(item.subCategory)}
                        onChange={handleCheckboxChange}
                        name={item.subCategory}
                        disabled={shouldDisableCheckbox(item)}
                      />
                    }
                    label={item.subCategory}
                  />
                ))}
              </Box>
              <Box sx={{ minWidth: 220, ml: 4 }}>
                {renderRegionCheckboxes("model", "Model Availability")}
                {renderRegionCheckboxes("material", "Material Availability")}
              </Box>
            </Box>
          ) : (
            <>Are you sure you want to download?</>
          )}
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Button
            sx={{ marginLeft: "15px" }}
            className="cancel-button"
            variant="outlined"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            className="add-Button"
            variant="contained"
            disabled={disabled}
            onClick={handleDownload}
            sx={{ backgroundColor: disabled ? "#d3d3d3" : '#0278ab !important', borderColor: disabled ? "#d3d3d3" : '#0278ab !important' }}

          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
};

export default DownloadDialog;
